import { api } from "@/lib/apiClient";

export const LANGUAGES = [
  ["en", "English"], ["bn", "বাংলা"], ["hi", "हिन्दी"], ["ur", "اردو"], ["ar", "العربية"],
  ["es", "Español"], ["pt", "Português"], ["fr", "Français"], ["de", "Deutsch"], ["it", "Italiano"],
  ["ru", "Русский"], ["tr", "Türkçe"], ["id", "Bahasa Indonesia"], ["zh-CN", "中文"], ["ja", "日本語"],
  ["ko", "한국어"], ["vi", "Tiếng Việt"], ["th", "ไทย"], ["fa", "فارسی"], ["sw", "Kiswahili"],
];
const CODES = new Set(LANGUAGES.map(([c]) => c));
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT", "SELECT", "OPTION", "SVG", "KBD"]);
const USER_CONTENT = /(release|track|song|artist|ticket|thread|lyric|smart-?link|playlist).*(title|name|msg|message|body|text)|artist-name|song-title|user-email|user-name|display-id/i;
const NOT_TEXT = /^([\w.+-]+@[\w-]+\.[\w.]+|https?:\/\/\S+|(release|user)_id_\d+|[\d\s.,:%$€£৳+\-/×x#()]+)$/i;
const ATTRS = ["placeholder", "title", "aria-label"];
const BRAND = /^(DistributeMusic(\.AI)?|\.AI|Distro AI|AI Studio|Suno|Udio|Spotify|Apple Music|TikTok|YouTube|Facebook|Instagram)$/i;

let lang = "en";
const mem = {};
const failed = new Map();  // key -> attempts; retried while the server finishes AI translation
const textRec = new WeakMap();
const attrRec = new WeakMap();
const tracked = new Set();
const queue = new Set();
const listeners = new Set();
let observer = null;
let flushTimer = null;
let scanTimer = null;

const isAdminPath = () => window.location.pathname.startsWith("/admin");
const active = () => lang !== "en" && !isAdminPath();

function loadCache(l) {
  if (mem[l]) return mem[l];
  try { mem[l] = JSON.parse(localStorage.getItem(`dm_i18n_${l}`) || "{}"); } catch { mem[l] = {}; }
  return mem[l];
}
function saveCache(l, entries) {
  try {
    const stored = JSON.parse(localStorage.getItem(`dm_i18n_${l}`) || "{}");
    Object.assign(stored, entries);
    const keys = Object.keys(stored);
    if (keys.length > 4000) keys.slice(0, keys.length - 4000).forEach((k) => delete stored[k]);
    localStorage.setItem(`dm_i18n_${l}`, JSON.stringify(stored));
  } catch { /* storage full — memory cache still works */ }
}

function skipEl(el) {
  if (!el || SKIP_TAGS.has(el.tagName)) return true;
  if (el.isContentEditable || el.closest('[translate="no"],.notranslate,[data-no-translate]')) return true;
  const tid = el.closest("[data-testid]")?.getAttribute("data-testid") || "";
  return USER_CONTENT.test(tid);
}
const wanted = (s) => s.length > 1 && s.length <= 5000 && /\p{L}/u.test(s) && !NOT_TEXT.test(s) && !BRAND.test(s);

function lookup(src) {
  const t = loadCache(lang)[src];
  if (t) return t;
  if ((failed.get(`${lang}\u0000${src}`) || 0) < 8) { queue.add(src); scheduleFlush(); }
  return null;
}

function doText(node) {
  const cur = node.nodeValue;
  let rec = textRec.get(node);
  if (rec && cur === rec.applied) return;
  if (!rec || cur !== rec.applied) { rec = { src: cur, applied: null }; textRec.set(node, rec); }
  const s = cur.trim();
  if (!wanted(s) || skipEl(node.parentElement)) return;
  tracked.add(node);
  const t = lookup(s);
  if (t) {
    rec.applied = cur.replace(s, t);
    node.nodeValue = rec.applied;
  }
}

function doAttrs(el) {
  if (el.closest('[translate="no"],.notranslate,[data-no-translate]')) return;
  let rec = attrRec.get(el);
  ATTRS.forEach((a) => {
    const cur = el.getAttribute(a);
    if (!cur) return;
    rec = rec || {};
    const r = rec[a];
    if (r && cur === r.applied) return;
    const entry = { src: cur, applied: null };
    rec[a] = entry;
    attrRec.set(el, rec);
    const s = cur.trim();
    if (!wanted(s)) return;
    tracked.add(el);
    const t = lookup(s);
    if (t) { entry.applied = t; el.setAttribute(a, t); }
  });
}

function scan(root = document.body) {
  if (!active() || !root) return;
  if (root.nodeType === 3) { doText(root); return; }
  if (root.nodeType !== 1 || skipEl(root) && root !== document.body) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (n) => (n.nodeType === 1 && SKIP_TAGS.has(n.tagName) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  for (let n = walker.currentNode; n; n = walker.nextNode()) {
    if (n.nodeType === 3) doText(n);
    else if (n.hasAttribute && ATTRS.some((a) => n.hasAttribute(a))) doAttrs(n);
  }
}
const scheduleScan = () => { clearTimeout(scanTimer); scanTimer = setTimeout(() => scan(), 60); };

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, 120);
}
async function flush() {
  flushTimer = null;
  const l = lang;
  const batch = [...queue].slice(0, 120);
  batch.forEach((s) => queue.delete(s));
  if (!batch.length || l === "en") return;
  try {
    const { data } = await api.post("/i18n/translate", { lang: l, texts: batch });
    const cache = loadCache(l);
    const aiOnly = {};
    Object.entries(data.items || {}).forEach(([src, v]) => { cache[src] = v.t; if (v.s === "ai") aiOnly[src] = v.t; });
    const miss = batch.filter((s) => !cache[s]);
    miss.forEach((s) => { const k = `${l}\u0000${s}`; failed.set(k, (failed.get(k) || 0) + 1); });
    saveCache(l, aiOnly);
    if (miss.length) {
      const tries = Math.max(...miss.map((s) => failed.get(`${l}\u0000${s}`) || 1));
      setTimeout(() => { if (l === lang) scheduleScan(); }, Math.min(5000 * tries, 30000));
    }
  } catch {
    batch.forEach((s) => { const k = `${l}\u0000${s}`; failed.set(k, (failed.get(k) || 0) + 1); });
    setTimeout(() => { if (l === lang) scheduleScan(); }, 15000);
  }
  if (queue.size) scheduleFlush();
  if (l === lang) scheduleScan();
}

function restore() {
  tracked.forEach((n) => {
    if (n.nodeType === 3) {
      const rec = textRec.get(n);
      if (rec && rec.applied !== null && n.nodeValue === rec.applied) { n.nodeValue = rec.src; rec.applied = null; }
    } else {
      const rec = attrRec.get(n) || {};
      Object.entries(rec).forEach(([a, r]) => { if (r.applied !== null && n.getAttribute(a) === r.applied) { n.setAttribute(a, r.src); r.applied = null; } });
    }
  });
  tracked.clear();
}

function startObserver() {
  if (observer) return;
  observer = new MutationObserver((muts) => {
    if (!active()) return;
    for (const m of muts) {
      if (m.type === "characterData") doText(m.target);
      else if (m.type === "attributes") doAttrs(m.target);
      else m.addedNodes.forEach((n) => scan(n));
    }
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
}

export const getLang = () => lang;
export const onLangChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

export function setLang(next, persist = true) {
  const l = CODES.has(next) ? next : "en";
  if (persist) { try { localStorage.setItem("dm_lang", l); } catch { /* ignore */ } }
  if (l === lang) return;
  restore();
  lang = l;
  document.documentElement.lang = l;
  listeners.forEach((fn) => fn(l));
  if (active()) { startObserver(); scan(); }
}

// Route changed: admin pages stay English, other pages get (re)translated.
export function onRoute() {
  document.documentElement.lang = isAdminPath() ? "en" : lang;
  if (isAdminPath()) restore();
  else if (active()) { startObserver(); scheduleScan(); }
}

export async function initTranslator() {
  let saved = null;
  try { saved = localStorage.getItem("dm_lang"); } catch { /* ignore */ }
  if (saved) { setLang(saved, false); return; }
  try {
    let detected = sessionStorage.getItem("dm_lang_detected");
    if (!detected) {
      const { data } = await api.get("/i18n/detect");
      detected = data.lang || "en";
      sessionStorage.setItem("dm_lang_detected", detected);
    }
    setLang(detected, false);
  } catch { /* stay English */ }
}
